import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { SignIn, useAuth } from '@clerk/clerk-react';
import AdminLayout from './layouts/AdminLayout';
import ProtectedRoute from './components/auth/ProtectedRoute';
import AdminRoleGuard from './components/auth/AdminRoleGuard';
import Overview from './pages/dashboard/Overview';

// Lazy load pages
import { lazy, Suspense, useEffect, useState } from 'react';
import LoadingSpinner from './components/common/LoadingSpinner';
import { DEV_AUTH_BYPASS } from './devBypass';

const Agencies = lazy(() => import('./pages/agencies/AgenciesList'));
const AgencyDetail = lazy(() => import('./pages/agencies/AgencyDetail'));
const TenantExplorer = lazy(() => import('./pages/tenants/TenantExplorer'));
const Tenants = lazy(() => import('./pages/tenants/TenantsList'));
const TenantDetail = lazy(() => import('./pages/tenants/TenantDetail'));
const Applications = lazy(() => import('./pages/applications/ApplicationsList'));
const Users = lazy(() => import('./pages/users/UsersList'));
const AdminUsersManage = lazy(() => import('./pages/users/AdminUsersManage'));
const Billing = lazy(() => import('./pages/billing/BillingOverview'));
const Support = lazy(() => import('./pages/support/SupportTickets'));
const AuditLogs = lazy(() => import('./pages/audit/AuditLogs'));
const SystemHealth = lazy(() => import('./pages/system/SystemHealth'));
const IntegrationsHealth = lazy(() => import('./pages/integrations/IntegrationsHealth'));
const PlansCatalog = lazy(() => import('./pages/plans/PlansCatalog'));
const ActivityFeed = lazy(() => import('./pages/activity/ActivityFeed'));
const AlertsPage = lazy(() => import('./pages/alerts/AlertsPage'));

const ADMIN_SESSION_KEY = 'lumiadmin_session';

function App() {
  const { isLoaded, isSignedIn, userId } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [, setAdminSessionValid] = useState(false);

  // Any valid Clerk session passes this gate; AdminRoleGuard enforces the real
  // platform-admin role. (Previously this force-signed-out any session that
  // didn't arrive with __clerk_ URL params, which looped forever with the
  // embedded <SignIn/> — it completes without setting those params.)
  useEffect(() => {
    if (DEV_AUTH_BYPASS) return; // local preview: skip all Clerk session handling
    if (!isLoaded) return;

    console.log('[LumiAdmin] Auth check:', { isSignedIn, userId });

    if (isSignedIn && userId) {
      sessionStorage.setItem(ADMIN_SESSION_KEY, userId);
      setAdminSessionValid(true);
      // Clean up Clerk ticket params left in the URL after a fresh sign-in
      if (location.search.includes('__clerk_') || location.hash.includes('/verify')) {
        window.history.replaceState({}, '', '/dashboard');
        navigate('/dashboard', { replace: true });
      }
    } else {
      sessionStorage.removeItem(ADMIN_SESSION_KEY);
      setAdminSessionValid(false);
    }
  }, [isLoaded, isSignedIn, userId, location.search, location.hash, navigate]);

  // Show loading while Clerk initializes
  if (!DEV_AUTH_BYPASS && !isLoaded) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading LumiAdmin...</p>
        </div>
      </div>
    );
  }

  // Show sign-in if not authenticated
  if (!DEV_AUTH_BYPASS && !isSignedIn) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="w-full max-w-md mx-auto text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">LumiAdmin Portal</h1>
          <p className="text-gray-600 mb-6">Please sign in to access the admin panel</p>
          <SignIn
            appearance={{
              elements: {
                rootBox: "mx-auto w-full",
                card: "shadow-xl w-full"
              }
            }}
          />
        </div>
      </div>
    );
  }

  // Signed in - show the app
  return (
    <Routes>
      <Route path="/sign-in" element={<Navigate to="/dashboard" replace />} />
      <Route
        path="/*"
        element={
          <ProtectedRoute>
            <AdminRoleGuard>
              <AdminLayout>
                <Suspense fallback={<LoadingSpinner />}>
                  <Routes>
                    <Route path="/dashboard" element={<Overview />} />
                    <Route path="/agencies" element={<Agencies />} />
                    <Route path="/agencies/:id" element={<AgencyDetail />} />
                    <Route path="/tenants" element={<TenantExplorer />} />
                    <Route path="/tenants/table" element={<Tenants />} />
                    <Route path="/tenants/:id" element={<TenantDetail />} />
                    <Route path="/applications" element={<Applications />} />
                    <Route path="/users" element={<AdminUsersManage />} />
                    <Route path="/users/all" element={<Users />} />
                    <Route path="/billing" element={<Billing />} />
                    <Route path="/support" element={<Support />} />
                    <Route path="/integrations" element={<IntegrationsHealth />} />
                    <Route path="/plans" element={<PlansCatalog />} />
                    <Route path="/activity" element={<ActivityFeed />} />
                    <Route path="/alerts" element={<AlertsPage />} />
                    <Route path="/audit-logs" element={<AuditLogs />} />
                    <Route path="/system" element={<SystemHealth />} />
                    <Route path="/" element={<Navigate to="/dashboard" replace />} />
                    <Route path="*" element={<Navigate to="/dashboard" replace />} />
                  </Routes>
                </Suspense>
              </AdminLayout>
            </AdminRoleGuard>
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}

export default App;
